import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchContentAssetBlob } from "../api/adminClient";

interface AdminAssetImageProps {
  contentHash: string;
  /** Thumbnail size class, default w-12 h-12 */
  thumbClassName?: string;
  /** Allow click to open lightbox */
  zoomable?: boolean;
}

export default function AdminAssetImage({
  contentHash,
  thumbClassName = "w-12 h-12",
  zoomable = true,
}: AdminAssetImageProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setSrc(null);
    fetchContentAssetBlob(contentHash)
      .then((blob) => {
        if (!active) return;
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setSrc(url);
        setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [contentHash]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  if (loading) {
    return (
      <div
        className={`${thumbClassName} bg-[var(--color-surface-container-low)] rounded-lg animate-pulse`}
      />
    );
  }

  if (!src) {
    return (
      <div
        className={`${thumbClassName} bg-[var(--color-surface-container-low)] rounded-lg flex items-center justify-center text-[10px] text-[var(--color-outline)]`}
      >
        图片
      </div>
    );
  }

  const thumb = (
    <img
      src={src}
      className={`${thumbClassName} object-cover rounded-lg border border-[var(--color-border-subtle)]${
        zoomable ? " cursor-zoom-in" : ""
      }`}
      alt="资源预览"
      onClick={zoomable ? () => setLightbox(true) : undefined}
    />
  );

  if (!lightbox) return thumb;

  return (
    <>
      {thumb}
      {createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4"
          onClick={() => setLightbox(false)}
          role="dialog"
          aria-modal="true"
          aria-label="图片预览"
        >
          <button
            type="button"
            className="absolute top-4 right-4 px-3 py-1.5 rounded-lg bg-black/50 text-white text-sm"
            onClick={() => setLightbox(false)}
          >
            关闭
          </button>
          <img
            src={src}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            alt="大图预览"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body,
      )}
    </>
  );
}
